import { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import ApiService from '../services/ApiService';

/**
 * Custom hook for bulk delete functionality
 * @param {string} endpoint - API endpoint for bulk delete (e.g., 'categories/bulk-delete')
 * @param {function} refreshData - Function to refresh the data after deletion
 * @returns {object} - { selectedIds, handleSelectAll, handleSelectItem, handleBulkDelete, isDeleting }
 */
const useBulkDelete = (endpoint, refreshData) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  // Toggle select all items
  const handleSelectAll = (items, idField = '_id') => {
    if (selectedIds.length === items.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map(item => item[idField]));
    }
  };

  // Toggle select single item
  const handleSelectItem = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(selectedId => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk delete selected items
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one item to delete');
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${selectedIds.length} item(s). This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: `Yes, delete ${selectedIds.length} item(s)!`,
      cancelButtonColor: '#9E9B9B',
      cancelButtonText: 'No, cancel',
    });

    if (result.isConfirmed) {
      setIsDeleting(true);
      try {
        const response = await ApiService.request({
          method: 'POST',
          url: endpoint,
          data: {
            ids: selectedIds
          },
        });

        const data = response.data;
        if (data.status) {
          toast.success(data.message || `${selectedIds.length} item(s) deleted successfully!`);
          setSelectedIds([]);
          if (refreshData) {
            refreshData();
          }
        } else {
          toast.error(data.message || 'Failed to delete items');
        }
      } catch (error) {
        console.error('Bulk delete error:', error);
        toast.error('Failed to delete items. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  return {
    selectedIds,
    handleSelectAll,
    handleSelectItem,
    handleBulkDelete,
    clearSelection,
    isDeleting,
    hasSelection: selectedIds.length > 0,
    selectionCount: selectedIds.length,
  };
};

export default useBulkDelete;

